import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  Self
} from '@angular/core';
import { FormControlName, NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputRootController } from '@shared/components/input/root/input-root-controller.directive';
import { InputType } from '@shared/components/input/root/input-type';

@Directive({
  selector: '[appInputDate]',
  providers: [{ provide: InputType, useExisting: InputDateDirective }],
})
export class InputDateDirective extends InputRootController implements AfterViewInit, OnDestroy {
  private sub?: Subscription;

  @Input() public defaultDate: Date | null | boolean = new Date();

  constructor(
    @Optional() @Self() @Inject(NgModel) protected override ngModel: NgModel,
    @Optional() @Self() @Inject(FormControlName) protected override formControl: FormControlName,
    protected override cdr: ChangeDetectorRef,
    protected override elementRef: ElementRef<HTMLInputElement>,
    private readonly _renderer: Renderer2
  ) {
    super(ngModel, formControl, cdr, elementRef);
  }

  public ngAfterViewInit(): void {
    if (this.elementRef.nativeElement.type !== 'date' && this.elementRef.nativeElement.type !== 'datetime-local') {
      this._renderer.setAttribute(this.elementRef.nativeElement, 'type', 'date');
    }

    const ctrl = this.formControl?.control ?? this.ngModel?.control;

    // Normalize current value (covers patchValue/initial binding)
    const initial = ctrl?.value ?? this.elementRef.nativeElement.value;
    if (initial != null && initial !== '') {
      const normalized = this.normalizeValueForCurrentType(initial);
      if (normalized === initial) {
        this.setNativeValue(normalized ?? '');
      } else {
        ctrl?.setValue(normalized, { emitEvent: false });
        this.setNativeValue(normalized ?? '');
      }
    }

    // Normalize subsequent updates from the form control
    if (ctrl) {
      this.sub = ctrl.valueChanges.subscribe((v) => {
        const normalized = this.normalizeValueForCurrentType(v);
        const isFocused = document.activeElement === this.elementRef.nativeElement;

        // When the user is actively editing, the browser may emit an intermediate
        // empty value (e.g. when "0" is typed into a date segment). Don't clear
        // the field in that case — wait for a complete, valid date or blur.
        if (normalized === null && isFocused) {
          return;
        }

        if (normalized !== v) {
          ctrl.setValue(normalized, { emitEvent: false });
        }
        if (this.elementRef.nativeElement.value !== (normalized ?? '')) {
          this.setNativeValue(normalized ?? '');
        }
      });
    }

    /**
     *  Set default value for date, if no value is set
     **/
    if (this.defaultDate && typeof this.defaultDate !== 'boolean' && !this.formControl?.value && !this.ngModel?.value) {
      const type = this.elementRef.nativeElement.type;
      const d = new Date(this.defaultDate);
      const defaultVal = type === 'datetime-local' ? this.formatAsLocalDateTime(d) : this.formatAsDateOnlyUTC(d);
      ctrl?.setValue(defaultVal, { emitEvent: false });
      this.setNativeValue(defaultVal);
    }
  }

  public ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private setNativeValue(v: string): void {
    this.elementRef.nativeElement.value = v;
  }

  private normalizeValueForCurrentType(value: unknown): string | null {
    const type = this.elementRef.nativeElement.type;
    return type === 'datetime-local' ? this.normalizeDateTimeLocalValue(value) : this.normalizeDateOnlyValue(value);
  }

  // Normalize to yyyy-MM-dd (date only)
  private normalizeDateOnlyValue(value: unknown): string | null {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const tIdx = value.indexOf('T');
      if (tIdx > 0) {
        const datePart = value.slice(0, tIdx);
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
      }
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return this.formatAsDateOnlyUTC(d);
      return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) return this.formatAsDateOnlyUTC(value);
    return null;
  }

  // Normalize to local yyyy-MM-ddTHH:mm for input[type="datetime-local"]
  private normalizeDateTimeLocalValue(value: unknown): string | null {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
      // Accept exact formats the control recognizes
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(value)) {
        // Trim seconds to minutes
        return value.substring(0, 16);
      }
      // Parse any ISO/parsable date and convert to local datetime without seconds
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) return this.formatAsLocalDateTime(d);
      return null;
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) return this.formatAsLocalDateTime(value);
    return null;
  }

  private formatAsDateOnlyUTC(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  private formatAsLocalDateTime(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${hh}:${mm}`;
  }
}
